import { useMsal } from '@azure/msal-react';
import { styled } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AppRouter from './AppRouter';
import PageTitle, { setPageTitle } from './components/DealGPT/PageTitle';
import { useAuth } from './components/Providers/AuthProvider';
import {
  getConversations,
  getFolderAgnosticConversations,
} from './redux/actions/conversationActions';
import { getFolders } from './redux/actions/folderActions';
import { getUsers } from './redux/actions/userActions';
import { selectAppStatus, setLLM } from './redux/reducers/appStatusReducer';
import { setMaxNumTokens } from './redux/reducers/conversationReducer';
import { AppDispatch } from './redux/store';
import { LlmEnum, llms } from './Types/llm';
import { useIsMobile } from './util/useIsMobile';

const FullPageContainer = styled('div')<{ isMobile: boolean }>(({ isMobile }) => ({
  height: isMobile ? '100%' : '100vh',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflowY: 'hidden',
}));

const ContentContainer = styled('div')({
  flex: '1 1 auto',
  overflowY: 'auto',
  position: 'relative',
  height: '100%',
});

setPageTitle();

function ProvidedApp() {
  const { currentUser } = useAuth();
  const isMobile = useIsMobile();

  const dispatch = useDispatch<AppDispatch>();
  const { llm } = useSelector(selectAppStatus);
  const { instance } = useMsal();

  const isExperts =
    import.meta.env.VITE_EXPERTS_REDIRECT_ORIGIN_URI &&
    window.location.origin === import.meta.env.VITE_EXPERTS_REDIRECT_ORIGIN_URI;

  const initialSetup = useCallback(() => {
    const foldersWithProjects =
      window.location.pathname.includes('intellio-advantage') ||
      window.location.pathname.includes('question-library');

    dispatch(
      getFolders({
        foldersWithProjects,
      })
    );
    dispatch(
      getFolderAgnosticConversations({
        foldersWithProjects,
      })
    );
    dispatch(getConversations({ pageAction: 'reset' }));
    dispatch(setLLM(currentUser?.metadata?.defaultLlm || LlmEnum.GPT_41));
    dispatch(getUsers({ ids: [] }));
  }, [dispatch, currentUser?.metadata?.defaultLlm]);

  useEffect(() => {
    // Don't run initial setup on login page
    if (window.location.pathname !== '/login' && currentUser) {
      initialSetup();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, currentUser]);

  useEffect(() => {
    dispatch(setMaxNumTokens(llms[llm].contextLimit || 64000));
  }, [dispatch, llm]);

  return (
    <>
      <PageTitle />
      <FullPageContainer isMobile={isMobile}>
        <ContentContainer>
          <AppRouter isExperts={isExperts} />
        </ContentContainer>
      </FullPageContainer>
    </>
  );
}

export default ProvidedApp;
